import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { fetchApplicationCategory, deleteApplicationCategory } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function ApplicationCategoryDetailPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const basePath = `/strategy/${businessDomainId}/application-categories/${categoryId}`;

  const loadData = useCallback(async () => {
    try { setData(await fetchApplicationCategory(categoryId)); }
    catch (e) { notify.apiError(e); }
  }, [categoryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteApplicationCategory(categoryId);
      notify.success(t("message.delete.success"));
      navigate(`/strategy/${businessDomainId}/application-categories`, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  const associatedTypesText = (data.associatedApplicationTypes || [])
    .map((at) => at.applicationTypeName)
    .join(", ");

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("applicationCategory.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`${basePath}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>
      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("applicationCategory.applicationType")} value={associatedTypesText} /></Grid>
          <Grid size={12}><DetailField label={t("applicationCategory.name")} value={data.applicationCategoryName} /></Grid>
          <Grid size={12}><DetailField label={t("common.description")} value={data.applicationCategoryDescription} /></Grid>
          <Grid size={12}><DetailField label={t("common.url")} value={data.url} /></Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Button variant="outlined" startIcon={<AssessmentIcon />}
          onClick={() => navigate(`${basePath}/classification-criteria`)}>
          {t("applicationCategory.classificationCriteriaValues")}
        </Button>
        <Button variant="outlined" startIcon={<FormatListBulletedIcon />}
          onClick={() => navigate(`${basePath}/application-functions`)}>
          {t("applicationCategory.applicationFunctions")}
        </Button>
        <Button variant="outlined" startIcon={<SaveOutlinedIcon />}
          onClick={() => navigate(`${basePath}/data-categories`)}>
          {t("applicationCategory.dataCategories")}
        </Button>
      </Box>

      <Box sx={{ mt: 1 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/application-categories`)}>{t("common.back")}</Button>
      </Box>
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
    </Box>
  );
}
